import {format} from 'date-fns';
import {HttpStatusCode} from 'axios';

import {
  getRequest,
  BackendResponseError,
} from '../../common/backend';
import {getUserInfo, setUserPerms} from '../auth/actions';
import {
  startRequest,
  endRequest,
  openDialog,
  closeDialog,
  openModal,
  closeModal,
} from './actions';

/**
 * APIリクエスト開始 - 通信中表示 (ぐるぐるアイコン表示)
 * @return {function}
 */
const doStartRequest = () => {
  return async (dispatch) => {
    dispatch(startRequest());
  };
};

/**
 * APIリクエスト終了 - 通信中表示終了
 * @return {function}
 */
const doEndRequest = () => {
  return async (dispatch) => {
    dispatch(endRequest());
  };
};

/** ダイアログ種別 */
const DIALOG_TYPES = {
  info: 'I',
  warn: 'W',
  error: 'E',
  confirm: 'C',
  systemerror: 'S',
};

/**
 * メッセージ種別ごとのダイアログボタン情報。
 * @type {[type: string]: 'ok'|'okcancel'}
 */
const BUTTON_TYPES = {
  /** 情報 */
  I: 'ok',

  /** 警告 */
  W: 'okcancel',

  /** エラー */
  E: 'ok',

  /** 確認 */
  C: 'okcancel',

  /** システムエラー */
  S: 'error',
};

/**
 * @typedef MessageInfo メッセージ情報
 * @property {string} id メッセージID。'message.'部分は不要
 * @property {string[]} [values] メッセージ可変部分
 */

/**
 * @typedef DialogMessageInfo ダイアログメッセージ情報
 * @property {string|MessageInfo|(string|MessageInfo)[]}
 *           message メッセージ情報。文字列の場合メッセージID
 * @property {Function} [action] OKボタン押下時の処理
 * @property {Function} [cancelAction] キャンセルボタン押下時の処理
 * @property {'info'|'warn'|'error'|'confirm'|'systemerror'} [type] ダイアログ種別。
 * @property {boolean} [highPriority] 高優先度フラグ。
 */

/**
 * ダイアログ表示。
 * @param {DialogMessageInfo} ダイアログメッセージ情報
 * @return {void}
 */
export const doShowMessage = ({
  message, action, cancelAction, type, highPriority
}) => {
  // メッセージの型をMessageInfo[]に統一する
  const messages = createMessages(message);

  let messageType;
  if (type == null) {
    // ダイアログ種別が指定されていない場合、メッセージ末尾 1文字目から種別を取得
    const idLength = messages[0].id.length;
    messageType = messages[0].id.substring(idLength - 1, idLength);
  } else {
    // ダイアログ種別が指定されている場合は定義から種別を取得
    messageType = DIALOG_TYPES[type];
  }

  // メッセージIDにプレフィックスを追加
  setPrefix(messages);

  // ロギング
  const logs = messages
      .map((msg) => msg.id?.replace('message.', ''))
      .join(', ')
      + (highPriority ? ' (high)' : '');
  console.info('Show dialog', `type=${messageType}, message=${logs}`);

  const dialog = {
    type: BUTTON_TYPES[messageType],
    title: 'dialog.title.' + messageType,
    context: messages,
    action: action,
    cancelAction: cancelAction,
    open: true,
    address: window.location.pathname,
    highPriority: highPriority,
  };

  return (dispatch, getState) => {
    const {common} = getState();

    // 登録済みダイアログ情報を取得
    const beforeDialog = common.dialogInfo;

    // 前回メッセージなし/今回高優先度/今回低優先度かつ前回低優先度 の場合はダイアログ情報を上書き
    if (!beforeDialog || highPriority || !beforeDialog.highPriority) {
      dispatch(openDialog(dialog));
    }
  };
};

/**
 * メッセージの型変換。
 * メッセージをMessageInfo[]に変換して返却する。
 *
 * @param {string|MessageInfo|(string|MessageInfo)[]} message メッセージ情報
 * @return {MessageInfo[]} 変換後のメッセージ情報
 */
function createMessages(message) {
  // 配列の場合
  if (Array.isArray(message)) {
    const ret = [];
    for (const item of message) {
      if (typeof item === 'string') {
        // stringの場合はの場合はMessageInfo化
        ret.push({id: item});
      } else {
        // MessageInfoの場合はコピーして設定
        // メッセージを上書きしてしまうため。
        const copyItem = {};
        Object.assign(copyItem, item);
        ret.push(copyItem);
      }
    }

    return ret;
  }

  // string型の場合はMessageInfo[]化して返却
  if (typeof message === 'string') {
    return [{id: message}];
  }

  // object型の場合(MessageInfo)は配列化して返却
  return [message];
}

/**
 * プレフィックスの付加。
 * メッセージIDの先頭にプレフィックス"message."を付加する
 *
 * @param {MessageInfo[]} messages メッセージ一覧
 */
function setPrefix(messages) {
  for (const item of messages) {
    item.id = 'message.' + item.id;
  }
}

const modalInfo = {
  btnExist: false,
  open: false,
};

const doOpenModal = (btnExist) => {
  modalInfo.btnExist = btnExist;
  modalInfo.open = true;
  return (dispatch) => {
    dispatch(openModal(modalInfo));
  };
};

const doCloseModal = () => {
  return (dispatch) => {
    dispatch(closeModal());
  };
};

/**
 * ログインユーザ情報の取得.
 *
 * @return {Promise<object>} ログインユーザ情報
 */
export const getCurrentUser = async () => {
  const response = await getRequest(null, 'login/user');

  if (response.status != HttpStatusCode.Ok) {
    throw new BackendResponseError('Failed to load login user info', response);
  }

  const userInfo = response.data;
  return userInfo;
};

/**
 * ユーザー権限取得
 */
const getPermissions = async () => {

  // 権限一覧取得
  const response = await getRequest({}, 'login/perms');
  if (response.status != '200') {
    return null;
  }
  const body = response.data;

  // 権限IDと権限種別のマップに変換
  const perms = Array.isArray(body) ? body : [];
  return Object.fromEntries(perms
      .filter((perm) => Object.hasOwn(perm, 'permId'))
      .map((perm) => [perm.permId, perm.permType]));
};

/**
 * ログイン時必要情報の一括取得処理。
 * @return {function}
 */
const doGetLoginInfo = () => {
  return async (dispatch) => {
    const loginUser = await getCurrentUser();
    const userPerms = await getPermissions();

    // ユーザ情報をストアへ登録
    dispatch(getUserInfo(loginUser));

    // ユーザー権限をストアへ登録
    dispatch(setUserPerms(userPerms));
    return loginUser;
  };
};

export default {
  startRequest,
  endRequest,
  doStartRequest,
  doEndRequest,
  doShowMessage,
  closeDialog,
  doOpenModal,
  doCloseModal,
  doGetLoginInfo,
};
